import Header from '@risk-smart/themed-cloudscape-components/header';
import type { FC, ReactNode } from 'react';

import styles from './drillDownColumn.module.scss';

interface Props {
  title: string;
  /**
   * Result count, already formatted for display (`(4)`). Omit while the column is
   * loading or waiting for a parent selection — a `(0)` beside a prompt to select a
   * parent reads as a result rather than a waiting state.
   */
  counter?: string;
  /** The permission-gated create control, or nothing when the user may not create. */
  action?: ReactNode;
}

/**
 * A drill-down column's header: category title, result count, create action.
 *
 * Shared by the Compliance, Risk and Internal Audit dashboards, which are three
 * clones of the same left-to-right cascade. Pure on purpose — the layout is fiddly
 * enough to be worth iterating on in Storybook rather than through a deploy, and
 * worth having Chromatic watch (`DrillDownColumnHeader.stories.tsx`).
 *
 * No hook or container alongside it: there is no state, no data and no domain here,
 * only markup. The geometry it depends on lives in `drillDownColumn.module.scss` next
 * to the measurements it is based on.
 *
 * `headingTagOverride` is set because every consumer renders this directly beneath the
 * page `h1`, so a plain `h3` would skip a level in the outline. The card titles inside
 * the columns stay `h3`, giving h1 > h2 > h3.
 */
export const DrillDownColumnHeader: FC<Props> = ({
  title,
  counter,
  action,
}) => (
  <div className={styles.columnHeader}>
    <Header
      variant={'h3'}
      headingTagOverride={'h2'}
      counter={counter}
      actions={action}
    >
      {title}
    </Header>
  </div>
);
